import axios from "@/plugins/axios";

/**
 * Normalize a command object from the API.
 * The backend sends the real schema in `variables_schema_json` (a JSON string)
 * while `variables_schema` is always an empty array.
 * Handles two JSON formats:
 *   - array of strings:  ["password"]
 *   - array of objects:  [{ "name": "password", ... }]
 */
function normalizeCommand(cmd) {
  let schema = [];

  if (Array.isArray(cmd.variables_schema) && cmd.variables_schema.length > 0) {
    // Already populated — use as-is (future-proof)
    schema = cmd.variables_schema.map((item) =>
      typeof item === "string" ? item : item.name || String(item)
    );
  } else if (cmd.variables_schema_json) {
    try {
      const parsed = JSON.parse(cmd.variables_schema_json);
      if (Array.isArray(parsed)) {
        schema = parsed.map((item) =>
          typeof item === "string" ? item : item.name || String(item)
        );
      }
    } catch (e) {
      // malformed JSON — leave schema empty
    }
  }

  return { ...cmd, variables_schema: schema };
}

function unwrapResponse(response) {
  const payload = response && response.data ? response.data : {};

  if (payload && payload.success === false) {
    const apiError = payload.error || {};
    const err = new Error(
      apiError.message || payload.message || "solar-actions-service-unavailable"
    );
    err.isApiError = true;
    err.code = apiError.code || null;
    err.fields = apiError.fields || null;
    err.status = response && response.status ? Number(response.status) : null;
    throw err;
  }

  if (Object.prototype.hasOwnProperty.call(payload, "data")) {
    return payload.data;
  }

  if (payload && payload.success === true) {
    return null;
  }

  if (payload && Object.prototype.hasOwnProperty.call(payload, "success")) {
    return payload.success;
  }

  return payload;
}

function parseApiError(err, fallbackMessageKey = "oops") {
  if (err && err.isApiError) {
    return {
      code: err.code || null,
      message: err.message || fallbackMessageKey,
      fields: err.fields || null,
      status: err.status || null,
      retryable: err.code === "SERVER_ERROR" || Number(err.status) === 502,
    };
  }

  const status =
    err && err.response && err.response.status
      ? Number(err.response.status)
      : null;
  const payload = err && err.response ? err.response.data || {} : {};
  const apiError = payload && payload.error ? payload.error : {};

  const code = apiError.code || null;
  const message =
    apiError.message || payload.message || err.message || fallbackMessageKey;
  const fields = apiError.fields || null;
  const retryable = code === "SERVER_ERROR" || status === 502;

  return { code, message, fields, status, retryable };
}

function isFresh(timestamp, maxAgeMs) {
  if (!timestamp) return false;
  return Date.now() - Number(timestamp) <= Number(maxAgeMs || 0);
}

function normalizeAttachmentItem(item, fallbackActionId = null) {
  if (!item || typeof item !== "object") return null;

  const pivot = item.pivot && typeof item.pivot === "object" ? item.pivot : {};
  const device =
    item.device && typeof item.device === "object" ? item.device : {};

  const slug =
    item.slug || item.device_slug || device.slug || pivot.device_slug || null;

  return {
    ...item,
    slug,
    id: item.id || fallbackActionId || null,
    device_name:
      item.device_name ||
      device.name ||
      (device.view && device.view.name) ||
      item.name ||
      "",
    device_mac:
      item.device_mac ||
      item.identifier ||
      device.identifier ||
      device.mac ||
      device.mac_address ||
      "",
    is_enabled:
      item.is_enabled !== undefined
        ? !!item.is_enabled
        : pivot.is_enabled !== false,
    external_device_id:
      item.external_device_id ||
      item.phone_number ||
      pivot.external_device_id ||
      pivot.phone_number ||
      null,
    variables: item.variables || pivot.variables || null,
    pivot,
  };
}

function normalizeSolarActionPayload(payload) {
  if (!payload || typeof payload !== "object") return payload;

  const action =
    payload.action && typeof payload.action === "object"
      ? payload.action
      : payload;

  const attachmentsRaw =
    action.attachments ||
    action.attached_devices ||
    action.devices ||
    action.device_actions ||
    action.device_attachments ||
    null;

  const normalizedAttachments = Array.isArray(attachmentsRaw)
    ? attachmentsRaw
        .map((item) => normalizeAttachmentItem(item, action.id || null))
        .filter(Boolean)
    : null;

  if (Array.isArray(normalizedAttachments)) {
    return {
      ...action,
      attachments: normalizedAttachments,
      attached_devices: normalizedAttachments,
    };
  }

  return action;
}

export const state = {
  list: [],
  loading: false,
  saving: false,
  smsCommands: [],
  smsCommandsLoading: false,
  smsCommandsFetchedAt: 0,
  listFetchedAt: 0,
  fetchedAtById: {},
};

export const mutations = {
  SET_LOADING(state, value) {
    state.loading = value;
  },
  SET_SAVING(state, value) {
    state.saving = value;
  },
  SET_SOLAR_ACTIONS(state, actions) {
    state.list = Array.isArray(actions) ? actions : [];
  },
  SET_LIST_FETCHED_AT(state, ts) {
    state.listFetchedAt = Number(ts) || 0;
  },
  SET_ACTION_FETCHED_AT(state, { id, ts }) {
    if (!id) return;
    state.fetchedAtById = {
      ...state.fetchedAtById,
      [String(id)]: Number(ts) || 0,
    };
  },
  SET_SMS_COMMANDS(state, commands) {
    state.smsCommands = Array.isArray(commands) ? commands : [];
  },
  SET_SMS_COMMANDS_FETCHED_AT(state, ts) {
    state.smsCommandsFetchedAt = Number(ts) || 0;
  },
  SET_SMS_COMMANDS_LOADING(state, value) {
    state.smsCommandsLoading = value;
  },
  UPSERT_SOLAR_ACTION(state, action) {
    if (!action || !action.id) return;
    const list = Array.isArray(state.list) ? [...state.list] : [];
    const index = list.findIndex(
      (item) => Number(item.id) === Number(action.id)
    );
    if (index === -1) {
      list.unshift(action);
    } else {
      list.splice(index, 1, action);
    }
    state.list = list;
    state.fetchedAtById = {
      ...state.fetchedAtById,
      [String(action.id)]: Date.now(),
    };
  },
};

export const actions = {
  async fetchSolarActions({ commit }) {
    commit("SET_LOADING", true);
    try {
      const res = await axios.get("/actions");
      const data = unwrapResponse(res);
      const list = Array.isArray(data)
        ? data.map((item) => normalizeSolarActionPayload(item))
        : [];
      commit("SET_SOLAR_ACTIONS", list || []);
      commit("SET_LIST_FETCHED_AT", Date.now());
      return { ok: true, data: list || [] };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("fetchSolarActions failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: apiError.retryable
          ? "solar-actions-service-unavailable"
          : apiError.message,
      });
      return { ok: false, error: apiError };
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchSolarActionsIfNeeded(
    { dispatch, getters },
    { force = false, maxAgeMs = 60000 } = {}
  ) {
    const existing = getters.solarActions;
    const fetchedAt = getters.solarActionsFetchedAt;

    if (
      !force &&
      Array.isArray(existing) &&
      fetchedAt &&
      isFresh(fetchedAt, maxAgeMs)
    ) {
      return { ok: true, data: existing, cached: true };
    }

    return dispatch("fetchSolarActions");
  },

  async createSolarAction({ commit, dispatch }, payload) {
    commit("SET_SAVING", true);
    try {
      const res = await axios.post("/actions/new", payload);
      const data = unwrapResponse(res);
      if (data && data.id) {
        commit("UPSERT_SOLAR_ACTION", data);
      }
      dispatch("fetchSolarActions");
      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-save-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("createSolarAction failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: apiError.retryable
          ? "solar-actions-service-unavailable"
          : apiError.message,
      });
      return { ok: false, error: apiError };
    } finally {
      commit("SET_SAVING", false);
    }
  },

  async editSolarAction({ commit, dispatch }, { id, payload }) {
    commit("SET_SAVING", true);
    try {
      const res = await axios.post(`/actions/${id}/edit`, payload);
      const data = unwrapResponse(res);
      if (data && data.id) {
        commit("UPSERT_SOLAR_ACTION", data);
      }
      dispatch("fetchSolarActions");
      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-save-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("editSolarAction failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: apiError.retryable
          ? "solar-actions-service-unavailable"
          : apiError.message,
      });
      return { ok: false, error: apiError };
    } finally {
      commit("SET_SAVING", false);
    }
  },

  async deactivateSolarAction({ commit, dispatch }, id) {
    commit("SET_SAVING", true);
    try {
      const res = await axios.post(`/actions/${id}/delete`);
      const data = unwrapResponse(res);
      if (data && data.id) {
        commit("UPSERT_SOLAR_ACTION", data);
      }
      dispatch("fetchSolarActions");
      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-deactivate-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("deactivateSolarAction failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: apiError.retryable
          ? "solar-actions-service-unavailable"
          : apiError.message,
      });
      return { ok: false, error: apiError };
    } finally {
      commit("SET_SAVING", false);
    }
  },

  async fetchSmsCommands({ commit }, deviceTypeId) {
    commit("SET_SMS_COMMANDS_LOADING", true);
    try {
      const params = deviceTypeId
        ? `?device_type_id=${encodeURIComponent(deviceTypeId)}`
        : "";
      const res = await axios.get(`/actions/sms-commands${params}`);
      const data = unwrapResponse(res);
      const raw = data && Array.isArray(data.commands) ? data.commands : [];
      const commands = raw.map(normalizeCommand);
      commit("SET_SMS_COMMANDS", commands);
      commit("SET_SMS_COMMANDS_FETCHED_AT", Date.now());
      return { ok: true, data: commands };
    } catch (err) {
      console.error("fetchSmsCommands failed", err);
      return { ok: false, error: parseApiError(err) };
    } finally {
      commit("SET_SMS_COMMANDS_LOADING", false);
    }
  },

  async fetchSmsCommandsIfNeeded(
    { dispatch, getters },
    { deviceTypeId = null, force = false, maxAgeMs = 60000 } = {}
  ) {
    const existing = getters.smsCommands;
    const fetchedAt = getters.smsCommandsFetchedAt;

    if (
      !force &&
      Array.isArray(existing) &&
      fetchedAt &&
      isFresh(fetchedAt, maxAgeMs)
    ) {
      return { ok: true, data: existing, cached: true };
    }

    return dispatch("fetchSmsCommands", deviceTypeId);
  },

  async fetchSolarAction({ commit }, id) {
    commit("SET_LOADING", true);
    try {
      const res = await axios.get(`/actions/${id}`);
      const rawData = unwrapResponse(res);
      const data = normalizeSolarActionPayload(rawData);
      if (data && data.id) {
        commit("UPSERT_SOLAR_ACTION", data);
        commit("SET_ACTION_FETCHED_AT", { id: data.id, ts: Date.now() });
      }
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("fetchSolarAction failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: apiError.retryable
          ? "solar-actions-service-unavailable"
          : apiError.message,
      });
      return { ok: false, error: apiError };
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchSolarActionIfNeeded(
    { dispatch, getters },
    { id, force = false, maxAgeMs = 60000 } = {}
  ) {
    if (!id) return { ok: false, error: { message: "Missing action id" } };
    const existing = getters.solarActionById(id);
    const fetchedAt = getters.solarActionFetchedAtById(id);

    if (!force && existing && isFresh(fetchedAt, maxAgeMs)) {
      return { ok: true, data: existing, cached: true };
    }

    return dispatch("fetchSolarAction", id);
  },
};

export const getters = {
  solarActions: (state) => state.list,
  solarActionById: (state) => (id) =>
    (state.list || []).find((item) => Number(item.id) === Number(id)) || null,
  loading: (state) => state.loading,
  saving: (state) => state.saving,
  smsCommands: (state) => state.smsCommands,
  smsCommandsLoading: (state) => state.smsCommandsLoading,
  smsCommandsFetchedAt: (state) => Number(state.smsCommandsFetchedAt || 0),
  solarActionFetchedAtById: (state) => (id) =>
    Number(state.fetchedAtById[String(id)] || 0),
  solarActionsFetchedAt: (state) => Number(state.listFetchedAt || 0),
};
